import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Spinner } from 'reactstrap';
import { ArrowLeft, Copy, Check, AlertTriangle, Inbox, UploadCloud } from 'react-feather';
import { apiError } from 'Shared/api';

/* ------------------------------------------------------------------ data loading */

/** Loads data with fn(); returns {data, loading, error, reload, setData}. */
export function useApi(fn, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const alive = useRef(true);
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fn();
      if (alive.current) setState({ data: res?.data ?? res, loading: false, error: null });
    } catch (err) {
      if (alive.current) setState((s) => ({ ...s, loading: false, error: apiError(err) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);
  const setData = (updater) =>
    setState((s) => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  return { ...state, reload: load, setData };
}

/* ------------------------------------------------------------------ layout */

export const Page = ({ title, subtitle, back, actions, children, narrow }) => (
  <div className={`ed-page ${narrow ? 'ed-page--narrow' : ''}`}>
    <header className="ed-page__head">
      <div className="ed-page__titles">
        {back && (
          <Link to={back.to} className="ed-page__back">
            <ArrowLeft size={16} aria-hidden="true" /> {back.label}
          </Link>
        )}
        <h1 className="ed-page__title">{title}</h1>
        {subtitle && <p className="ed-page__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="ed-page__actions">{actions}</div>}
    </header>
    <div className="ed-page__body">{children}</div>
  </div>
);

export const Panel = ({ title, description, actions, children, flush, className = '', tone, id }) => (
  <section id={id} className={`ed-panel ${flush ? 'ed-panel--flush' : ''} ${tone ? `ed-panel--${tone}` : ''} ${className}`}>
    {(title || actions) && (
      <div className="ed-panel__head">
        <div>
          {title && <h2 className="ed-panel__title">{title}</h2>}
          {description && <p className="ed-panel__desc">{description}</p>}
        </div>
        {actions && <div className="ed-panel__actions">{actions}</div>}
      </div>
    )}
    <div className="ed-panel__body">{children}</div>
  </section>
);

export const Grid = ({ min = 240, children, className = '' }) => (
  <div className={`ed-grid ${className}`} style={{ '--ed-grid-min': `${min}px` }}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ status */

const TONES = {
  verified: 'ok', approved: 'ok', active: 'ok', ok: 'ok', resolved: 'ok', success: 'ok', ready: 'ok', allowed: 'ok',
  submitted: 'wait', pending: 'wait', pending_verification: 'wait', awaiting_review: 'wait', acknowledged: 'wait',
  submitted_to_telco: 'info', preparing: 'neutral', missing: 'neutral', waived: 'info', open: 'bad',
  rejected: 'bad', suspended: 'bad', failed: 'bad', expired: 'bad', closed: 'neutral', withdrawn: 'neutral',
  unknown_accepted: 'wait', unknown: 'wait', inactive: 'neutral',
  critical: 'bad', high: 'bad', medium: 'wait', low: 'neutral',
};

const LABELS = {
  pending_verification: 'Pending verification',
  submitted: 'Awaiting review',
  submitted_to_telco: 'With network',
  unknown_accepted: 'Unknown (accepted)',
  missing: 'Not provided',
};

export const statusLabel = (s) => {
  if (!s) return '';
  if (LABELS[s]) return LABELS[s];
  const t = String(s).replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export const Pill = ({ status, tone, children, title }) => (
  <span className={`ed-pill ed-pill--${tone || TONES[String(status).toLowerCase()] || 'neutral'}`} title={title}>
    {children || statusLabel(status)}
  </span>
);

const TELCO = {
  MTN: { name: 'MTN', cls: 'mtn' },
  AIRTEL: { name: 'Airtel', cls: 'airtel' },
  GLO: { name: 'Glo', cls: 'glo' },
  NINEMOBILE: { name: '9mobile', cls: 'ninemobile' },
  '9MOBILE': { name: '9mobile', cls: 'ninemobile' },
};

export const telcoName = (t) => TELCO[String(t || '').toUpperCase()]?.name || t;

export const TelcoChip = ({ telco, children }) => {
  const t = TELCO[String(telco || '').toUpperCase()];
  return <span className={`ed-telco ed-telco--${t ? t.cls : 'other'}`}>{children || (t ? t.name : telco)}</span>;
};

/* ------------------------------------------------------------------ stats */

export const Stats = ({ items }) => (
  <dl className="ed-stats">
    {items.map((it) => (
      <div key={it.label} className={`ed-stat ${it.tone ? `ed-stat--${it.tone}` : ''}`}>
        <dt>{it.label}</dt>
        <dd>{it.value}</dd>
        {it.hint && <span className="ed-stat__hint">{it.hint}</span>}
      </div>
    ))}
  </dl>
);

/** A thin progress meter, e.g. 7 of 10 verified. */
export const Meter = ({ value, total, label, tone = 'ok' }) => {
  const p = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="ed-meter" role="img" aria-label={label || `${value} of ${total}`}>
      <div className={`ed-meter__fill ed-meter__fill--${tone}`} style={{ width: `${p}%` }} />
    </div>
  );
};

/* ------------------------------------------------------------------ table */

/**
 * Table on wide screens, stacked rows on phones (each cell labelled).
 * columns: [{key, label, render(row), align: 'end', primary: true (title line on mobile), hide: 'mobile'}]
 */
export const DataTable = ({ columns, rows, rowKey = 'id', empty, onRowClick, dense }) => {
  if (!rows || rows.length === 0) return empty || <Empty title="Nothing to show" />;
  return (
    <div className={`ed-table-wrap ${dense ? 'ed-table-wrap--dense' : ''}`}>
      <table className="ed-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`${c.align === 'end' ? 'ed-end' : ''} ${c.hide === 'mobile' ? 'ed-hide-sm' : ''}`} scope="col">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const key = typeof rowKey === 'function' ? rowKey(r, i) : r[rowKey] ?? i;
            return (
              <tr
                key={key}
                className={onRowClick ? 'ed-clickable' : ''}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={onRowClick ? (e) => (e.key === 'Enter' ? onRowClick(r) : null) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={c.label}
                    className={`${c.align === 'end' ? 'ed-end' : ''} ${c.primary ? 'ed-primary' : ''} ${c.hide === 'mobile' ? 'ed-hide-sm' : ''}`}
                  >
                    {c.render ? c.render(r) : r[c.key] ?? '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ------------------------------------------------------------------ feedback */

export const Empty = ({ title, children, action, icon: Icon = Inbox }) => (
  <div className="ed-empty">
    <Icon size={22} aria-hidden="true" />
    <p className="ed-empty__title">{title}</p>
    {children && <p className="ed-empty__text">{children}</p>}
    {action}
  </div>
);

export const Loading = ({ lines = 4 }) => (
  <div className="ed-loading" aria-busy="true" aria-label="Loading">
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="ed-loading__line" style={{ width: `${90 - i * 12}%` }} />
    ))}
  </div>
);

export const ErrorNote = ({ error, onRetry }) =>
  error ? (
    <div className="ed-error" role="alert">
      <AlertTriangle size={18} aria-hidden="true" />
      <span>{error}</span>
      {onRetry && (
        <button type="button" className="ed-btn ed-btn--ghost ed-btn--sm" onClick={() => onRetry()}>
          Try again
        </button>
      )}
    </div>
  ) : null;

export const Note = ({ tone = 'info', children, icon: Icon }) => (
  <div className={`ed-note ed-note--${tone}`}>
    {Icon && <Icon size={18} aria-hidden="true" />}
    <div>{children}</div>
  </div>
);

/* ------------------------------------------------------------------ actions */

export const Button = ({ variant = 'primary', size, busy, children, className = '', icon: Icon, ...rest }) => (
  <button
    type="button"
    className={`ed-btn ed-btn--${variant} ${size ? `ed-btn--${size}` : ''} ${className}`}
    disabled={busy || rest.disabled}
    {...rest}
  >
    {busy ? <Spinner size="sm" className="ed-btn__spin" /> : Icon ? <Icon size={16} aria-hidden="true" /> : null}
    <span>{children}</span>
  </button>
);

export const CopyButton = ({ text, label = 'Copy' }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch (e) {
      /* clipboard unavailable */
    }
  };
  return (
    <Button variant="ghost" size="sm" icon={done ? Check : Copy} onClick={copy}>
      {done ? 'Copied' : label}
    </Button>
  );
};

export const Code = ({ children, copy, label }) => (
  <div className="ed-code">
    {(copy || label) && (
      <div className="ed-code__bar">
        <span>{label}</span>
        {copy && <CopyButton text={typeof children === 'string' ? children : copy} />}
      </div>
    )}
    <pre>{children}</pre>
  </div>
);

export const Tabs = ({ tabs, value, onChange, label = 'Sections' }) => (
  <div className="ed-tabs" role="tablist" aria-label={label}>
    {tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        role="tab"
        aria-selected={value === t.key}
        className={`ed-tab ${value === t.key ? 'is-active' : ''}`}
        onClick={() => onChange(t.key)}
      >
        {t.label}
        {t.count !== undefined && t.count !== null && <span className="ed-tab__count">{t.count}</span>}
      </button>
    ))}
  </div>
);

/* ------------------------------------------------------------------ forms */

export const Field = ({ label, hint, error, children, htmlFor, required }) => (
  <div className={`ed-field ${error ? 'has-error' : ''}`}>
    {label && (
      <label className="ed-field__label" htmlFor={htmlFor}>
        {label}
        {required && <span className="ed-req" aria-hidden="true"> *</span>}
      </label>
    )}
    {children}
    {hint && !error && <p className="ed-field__hint">{hint}</p>}
    {error && <p className="ed-field__error">{error}</p>}
  </div>
);

export const FileInput = ({ id, onChange, multiple, accept = '.pdf,.jpg,.jpeg,.png', files, label = 'Choose file' }) => {
  const names = files && files.length ? Array.from(files).map((f) => f.name).join(', ') : null;
  return (
    <label className={`ed-file ${names ? 'has-file' : ''}`} htmlFor={id}>
      <UploadCloud size={18} aria-hidden="true" />
      <span className="ed-file__text">{names || label}</span>
      <span className="ed-file__meta">PDF, JPG or PNG, up to 5 MB</span>
      <input
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => onChange(multiple ? Array.from(e.target.files) : e.target.files[0])}
      />
    </label>
  );
};

/**
 * Confirmation dialog, optionally asking for a note (reason).
 * onConfirm(note) may return a promise; errors are shown in the dialog.
 */
export const ConfirmDialog = ({
  open, title, children, confirmLabel = 'Confirm', danger, noteLabel, noteRequired, notePlaceholder, onConfirm, onClose,
}) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (open) {
      setNote('');
      setErr(null);
      setBusy(false);
    }
  }, [open]);
  const submit = async () => {
    if (noteRequired && !note.trim()) {
      setErr(`${noteLabel || 'A reason'} is required.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(note.trim());
      onClose();
    } catch (e) {
      setErr(apiError(e));
      setBusy(false);
    }
  };
  return (
    <Modal isOpen={open} toggle={onClose} centered className="ed-modal">
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {children}
        {noteLabel && (
          <Field label={noteLabel} htmlFor="ed-dialog-note" required={noteRequired}>
            <Input
              id="ed-dialog-note"
              type="textarea"
              rows={3}
              value={note}
              placeholder={notePlaceholder}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        )}
        <ErrorNote error={err} />
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={submit}>
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const Toolbar = ({ children }) => <div className="ed-toolbar">{children}</div>;

export const KeyValue = ({ items }) => (
  <dl className="ed-kv">
    {items
      .filter((i) => i && i.value !== undefined && i.value !== null && i.value !== '')
      .map((i) => (
        <div key={i.label} className="ed-kv__row">
          <dt>{i.label}</dt>
          <dd>{i.value}</dd>
        </div>
      ))}
  </dl>
);
